import React, {
  forwardRef,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  LiHTMLAttributes,
  ReactElement,
  ReactNode,
  KeyboardEvent,
} from 'react';
import classNames from 'classnames';
import { useMenuContext } from './Menu';
import { menu_item_base_css, menu_group_item_css, getMenuItemStyles, getMenuGroupStyles } from './styles';
import { IconProps } from '../icons/generated';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';

export interface MenuItemProps extends Omit<LiHTMLAttributes<HTMLLIElement>, 'onClick'> {
  /** 그룹 메뉴 ID */
  groupId?: string;
  /** 메뉴 아이템 unique ID */
  id: string;
  /** 메뉴 라벨 */
  label: string;
  /** 메뉴 아이템 아이콘 */
  icon?: ReactElement<IconProps>;
  /** 메뉴 아이템 우측 영역에 렌더링 될 요소 */
  suffix?: ReactElement<IconProps> | ReactNode;
}

export const MenuIcon = ({
  selected,
  children,
}: PropsWithChildren<{ selected: boolean; children: ReactElement<IconProps> }>) => {
  return isValidElement(children)
    ? cloneElement(children, {
        color: selected ? semantic_colors.accent.primary : semantic_colors.content.secondary,
      })
    : null;
};

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  ({ className, groupId, id, label, icon, suffix, ...props }, ref) => {
    const { activeMenu, onClickMenu, dense } = useMenuContext();
    const selected = activeMenu === id;
    const color = selected ? semantic_colors.accent.primary : semantic_colors.content.primary;
    const textKind = groupId ? 'Body_13_Regular' : selected ? 'Body_13_SemiBold' : 'Body_13_Medium';
    const menuItemsStyles = groupId
      ? [menu_group_item_css, getMenuGroupStyles(dense)]
      : [menu_item_base_css, getMenuItemStyles(dense)];
    const handleSelect = () => onClickMenu({ id, groupId });
    const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect();
      }
    };

    return (
      <li
        role='menuitem'
        tabIndex={0}
        className={classNames('pds-menu-item', className)}
        aria-current={selected ? 'page' : undefined}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
        css={menuItemsStyles}
        {...props}
        ref={ref}
      >
        <Stack direction='row' align='center' gap={8}>
          {icon && <MenuIcon selected={selected}>{icon}</MenuIcon>}
          <Stack direction='row' align='center' gap={4}>
            <BaseText kind={textKind} color={color}>
              {label}
            </BaseText>
            {suffix}
          </Stack>
        </Stack>
      </li>
    );
  },
);
